import { useEffect, useState } from 'react';
import { orderBy } from 'firebase/firestore';
import { motion, AnimatePresence } from 'motion/react';
import {
  canProject,
  type DocumentVersion,
  type Job,
  type Regeneration,
  type RegenerationProposal,
} from '@allemnii-pulse/shared';
import { useCollection, useDocument } from '../../lib/firestore.js';
import { useProject } from '../projects/ProjectProvider.js';
import { api } from '../../lib/api.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../components/ui/Select.js';
import { Term } from '../../components/ui/Term.js';

function StartRegeneration({ onStarted }: { onStarted: (regenerationId: string) => void }) {
  const { projectId } = useProject();
  const [docType, setDocType] = useState<'prd' | 'tdd'>('prd');
  const { data: versions } = useCollection<DocumentVersion>(
    `projects/${projectId}/documents/${docType}/versions`,
    [orderBy('versionNumber', 'desc')],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const { data: job } = useDocument<Job>(jobId ? `jobs/${jobId}` : null);

  useEffect(() => {
    if (job?.status === 'succeeded' && job.result) {
      onStarted((job.result as { regenerationId: string }).regenerationId);
    } else if (job?.status === 'failed') {
      setError(job.error ?? 'Could not start regeneration');
      setJobId(null);
    }
  }, [job, onStarted]);

  const canCompare = versions.length >= 2;

  async function handleStart() {
    if (!canCompare) return;
    setBusy(true);
    setError(null);
    try {
      const { jobId: id } = await api.post<{ jobId: string }>(
        `/projects/${projectId}/regenerations`,
        {
          docType,
          fromVersionId: versions[1]!.id,
          toVersionId: versions[0]!.id,
        },
      );
      setJobId(id);
    } catch {
      setError('Could not start regeneration.');
      setBusy(false);
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4 sm:p-6">
      <h2 className="text-sm font-semibold">
        Check the latest changes for <Term id="regeneration">impact on tasks</Term>
      </h2>
      <div className="flex flex-wrap items-center gap-3">
        <Select value={docType} onValueChange={(v) => setDocType(v as 'prd' | 'tdd')}>
          <SelectTrigger className="h-9 w-auto text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="prd">PRD</SelectItem>
            <SelectItem value="tdd">TDD</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="default" disabled={busy || !canCompare} onClick={() => void handleStart()}>
          {busy ? 'Working…' : 'Start'}
        </Button>
        <span className="text-xs text-muted">
          {canCompare
            ? `Compares v${versions[1]!.versionNumber} → v${versions[0]!.versionNumber} (latest two versions)`
            : 'Needs at least two versions of this document.'}
        </span>
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}
    </Card>
  );
}

function ProposalCard({
  proposal,
  onDecide,
  canDecide,
}: {
  proposal: RegenerationProposal & { _regenerationId: string };
  onDecide: (id: string, decision: 'accepted' | 'rejected') => void;
  canDecide: boolean;
}) {
  const kindClass =
    proposal.kind === 'obsolete'
      ? 'text-muted'
      : proposal.kind === 'new'
        ? 'text-success'
        : 'text-accent-soft-fg';

  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs font-semibold uppercase ${kindClass}`}>{proposal.kind}</span>
        {proposal.taskId && (
          <span className="min-w-0 break-all font-mono text-xs text-muted">{proposal.taskId}</span>
        )}
        {proposal.conflictsWithHumanEdit && (
          <span className="rounded-full bg-[color-mix(in_srgb,var(--danger)_15%,transparent)] px-2 py-0.5 text-[10px] font-medium text-danger">
            hand-edited
          </span>
        )}
      </div>
      <p className="break-words text-sm">{proposal.reason}</p>
      {proposal.decision === 'pending' && !canDecide ? (
        <span className="self-end text-xs text-muted">Awaiting a project lead's decision</span>
      ) : proposal.decision === 'pending' ? (
        <div className="flex justify-end gap-2">
          <Button className="h-10 md:h-8" onClick={() => onDecide(proposal.id, 'rejected')}>
            Reject
          </Button>
          <Button
            variant="default"
            className="h-10 md:h-8"
            onClick={() => onDecide(proposal.id, 'accepted')}
          >
            Accept
          </Button>
        </div>
      ) : (
        <span
          className={`self-end text-xs font-medium ${proposal.decision === 'accepted' ? 'text-success' : 'text-muted'}`}
        >
          {proposal.decision}
        </span>
      )}
    </Card>
  );
}

export function RegenerationPage() {
  const { projectId, role } = useProject();
  const canDecide = canProject(role, 'regeneration.decide');
  const { data: regenerations } = useCollection<Regeneration>(
    `projects/${projectId}/regenerations`,
    [orderBy('createdAt', 'desc')],
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const active = activeId ?? regenerations[0]?.id ?? null;
  const { data: proposals } = useCollection<RegenerationProposal>(
    active ? `projects/${projectId}/regenerations/${active}/proposals` : null,
  );

  async function decide(proposalId: string, decision: 'accepted' | 'rejected') {
    if (!active) return;
    await api.post(
      `/projects/${projectId}/regenerations/${active}/proposals/${proposalId}/decide`,
      { decision },
    );
  }

  const pending = proposals.filter((p) => p.decision === 'pending').length;

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 sm:p-6">
      <div>
        <h1 className="text-lg font-semibold">Regeneration review</h1>
        <p className="text-sm text-muted">
          When the PRD or TDD changes, existing tasks are checked for impact. Nothing changes until
          you decide each item.
        </p>
      </div>

      <StartRegeneration onStarted={setActiveId} />

      {active && (
        <>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 p-3 text-xs text-muted">
            {pending} of {proposals.length} still to decide.
          </div>
          <div className="flex flex-col gap-3">
            <AnimatePresence initial={false}>
              {proposals.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, delay: i * 0.03 }}
                >
                  <ProposalCard
                    proposal={{ ...p, _regenerationId: active }}
                    onDecide={decide}
                    canDecide={canDecide}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </>
      )}
    </div>
  );
}
